import React, { useState, useEffect, useCallback } from 'react';
import { User, Phone, Mail, MapPin, Building2, Calendar, Shield, Edit2, Camera, Save, X, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import ListPageHeader from '../../components/shared/ListPageHeader';
import { staffSelfAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const MyProfilePage = () => {
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [editData, setEditData] = useState({});
  const [pwData, setPwData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwShow, setPwShow] = useState({ current: false, new: false, confirm: false });

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await staffSelfAPI.getMyProfile();
      if (data?.profile) {
        const profileData = {
          id: data.profile.id,
          name: data.profile.full_name,
          email: data.profile.email,
          phone: data.profile.mobile,
          employee_id: data.profile.employee_code,
          role: data.profile.role_template || 'Staff',
          status: data.profile.status,
          joined_at: data.profile.date_of_joining,
          profile_image: data.profile.profile_image,
          address: data.profile.address,
          emergency_contact: data.profile.emergency_contact,
          department: data.profile.department,
          designation: data.profile.designation,
        };
        setProfile(profileData);
        setEditData(profileData);
      } else {
        toast.error('Could not load profile data');
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load profile'));
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await staffSelfAPI.updateMyProfile({
        mobile: editData.phone,
        address: editData.address,
        emergency_contact: editData.emergency_contact,
      });
      toast.success('Profile updated successfully!');
      setProfile(editData);
      setEditing(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update profile'));
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!pwData.current_password || !pwData.new_password || !pwData.confirm_password) {
      toast.error('All password fields are required');
      return;
    }
    if (pwData.new_password !== pwData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (pwData.new_password.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    try {
      setPwSaving(true);
      await staffSelfAPI.changePassword({
        current_password: pwData.current_password,
        new_password: pwData.new_password,
      });
      toast.success('Password changed successfully');
      setPwData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to change password'));
    } finally {
      setPwSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '---';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  return (
    <div className="h-full flex flex-col" data-testid="my-profile-page">
      <ListPageHeader
        title="My Profile"
        subtitle="View and manage your profile information"
        stats={[
          { icon: User, label: 'Role', value: profile?.role || 'Staff', color: 'slate' },
          { icon: Building2, label: 'Department', value: profile?.department || 'Sales', color: 'blue' },
          { icon: Shield, label: 'Status', value: profile?.status === 'active' ? 'Active' : 'Inactive', color: profile?.status === 'active' ? 'emerald' : 'slate' },
        ]}
        ctaLabel={!editing ? 'Edit Profile' : undefined}
        ctaIcon={!editing ? Edit2 : undefined}
        onCtaClick={!editing ? () => setEditing(true) : undefined}
      />

      {/* Edit Actions */}
      {editing && (
        <div className="flex-shrink-0 px-2 pt-2">
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 flex items-center justify-between">
            <p className="text-[13px] font-medium text-blue-800">Editing mode enabled</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setEditing(false); setEditData(profile); }}
                className="px-3 py-1.5 text-[12px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                <Save className="w-3.5 h-3.5" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className="flex-1 overflow-auto px-2 pb-2 pt-2">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
              <p className="text-[13px] text-slate-500 font-normal">Loading profile...</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {/* Profile Card */}
            <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
              <div className="p-6 text-center border-b border-slate-100">
                <div className="relative inline-block mb-4">
                  <div className="w-20 h-20 rounded-full bg-teal-100 flex items-center justify-center">
                    {profile?.profile_image ? (
                      <img src={profile.profile_image} alt="Profile" className="w-20 h-20 rounded-full object-cover" />
                    ) : (
                      <span className="text-2xl font-medium text-teal-700">
                        {profile?.name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    )}
                  </div>
                  {editing && (
                    <button className="absolute bottom-0 right-0 w-7 h-7 bg-teal-600 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-teal-700 transition-colors">
                      <Camera className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <h2 className="text-[16px] font-semibold text-slate-800">{profile?.name || 'Staff Member'}</h2>
                <p className="text-[12px] text-slate-500 mt-0.5">{profile?.designation || profile?.role || 'Field Staff'}</p>
                <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 bg-teal-50 text-teal-700 rounded-full text-[11px] font-medium">
                  <Shield className="w-3 h-3" />
                  {profile?.status === 'active' ? 'Active' : 'Inactive'}
                </div>
              </div>
              <div className="p-4 space-y-2.5">
                <div className="flex items-center gap-3 text-[12px]">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">{profile?.department || 'Sales Department'}</span>
                </div>
                <div className="flex items-center gap-3 text-[12px]">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">Joined {formatDate(profile?.joined_at)}</span>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="lg:col-span-2 space-y-3">
              <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                  <h3 className="text-[14px] font-semibold text-slate-700">Contact Information</h3>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] text-slate-500 uppercase tracking-wider font-medium mb-1.5">Email</label>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span className="text-[13px] text-slate-700">{profile?.email || '---'}</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] text-slate-500 uppercase tracking-wider font-medium mb-1.5">Phone</label>
                    {editing ? (
                      <input
                        type="tel"
                        value={editData.phone || ''}
                        onChange={(e) => setEditData({...editData, phone: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 focus:outline-none"
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span className="text-[13px] text-slate-700">{profile?.phone || '---'}</span>
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-[11px] text-slate-500 uppercase tracking-wider font-medium mb-1.5">Address</label>
                    {editing ? (
                      <textarea
                        value={editData.address || ''}
                        onChange={(e) => setEditData({...editData, address: e.target.value})}
                        rows={2}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 focus:outline-none resize-none"
                      />
                    ) : (
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                        <span className="text-[13px] text-slate-700">{profile?.address || '---'}</span>
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-[11px] text-slate-500 uppercase tracking-wider font-medium mb-1.5">Emergency Contact</label>
                    {editing ? (
                      <input
                        type="tel"
                        value={editData.emergency_contact || ''}
                        onChange={(e) => setEditData({...editData, emergency_contact: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 focus:outline-none"
                        placeholder="Enter emergency contact number"
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span className="text-[13px] text-slate-700">{profile?.emergency_contact || '---'}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Work Information */}
              <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                  <h3 className="text-[14px] font-semibold text-slate-700">Work Information</h3>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] text-slate-500 uppercase tracking-wider font-medium mb-1.5">Employee ID</label>
                    <span className="text-[13px] text-slate-700">{profile?.employee_id || profile?.id?.slice(0, 8) || '---'}</span>
                  </div>
                  <div>
                    <label className="block text-[11px] text-slate-500 uppercase tracking-wider font-medium mb-1.5">Department</label>
                    <span className="text-[13px] text-slate-700">{profile?.department || '---'}</span>
                  </div>
                  <div>
                    <label className="block text-[11px] text-slate-500 uppercase tracking-wider font-medium mb-1.5">Reporting To</label>
                    <span className="text-[13px] text-slate-700">{profile?.reporting_to || '---'}</span>
                  </div>
                  <div>
                    <label className="block text-[11px] text-slate-500 uppercase tracking-wider font-medium mb-1.5">Assigned Areas</label>
                    <span className="text-[13px] text-slate-700">{profile?.assigned_areas?.join(', ') || '---'}</span>
                  </div>
                </div>
              </div>

              {/* Change Password */}
              <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2">
                  <Lock className="w-4 h-4 text-slate-500" />
                  <h3 className="text-[14px] font-semibold text-slate-700">Change Password</h3>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: 'current_password', label: 'Current Password', showKey: 'current' },
                    { key: 'new_password', label: 'New Password', showKey: 'new' },
                    { key: 'confirm_password', label: 'Confirm New Password', showKey: 'confirm' },
                  ].map(({ key, label, showKey }) => (
                    <div key={key} className={key === 'current_password' ? 'md:col-span-2' : ''}>
                      <label className="block text-[11px] text-slate-500 uppercase tracking-wider font-medium mb-1.5">{label}</label>
                      <div className="relative">
                        <input
                          type={pwShow[showKey] ? 'text' : 'password'}
                          value={pwData[key]}
                          onChange={(e) => setPwData({ ...pwData, [key]: e.target.value })}
                          className="w-full px-3 py-2 pr-9 border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 focus:outline-none"
                          placeholder={`Enter ${label.toLowerCase()}`}
                        />
                        <button
                          type="button"
                          onClick={() => setPwShow({ ...pwShow, [showKey]: !pwShow[showKey] })}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {pwShow[showKey] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="md:col-span-2 flex justify-end">
                    <button
                      onClick={handleChangePassword}
                      disabled={pwSaving}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-[12px] font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50"
                    >
                      <Lock className="w-3.5 h-3.5" />
                      {pwSaving ? 'Updating...' : 'Update Password'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyProfilePage;
